import React, { useState } from 'react';
import '@galaxy/client/index.css';
import { GameProvider, useGame } from '@galaxy/client/state/gameStore.tsx';
import { Header } from '@galaxy/client/components/Header.tsx';
import { Navigation, type TabType } from '@galaxy/client/components/Navigation.tsx';
import { BaseView } from '@galaxy/client/components/BaseView.tsx';
import { ResearchView } from '@galaxy/client/components/ResearchView.tsx';
import { ShipyardView } from '@galaxy/client/components/ShipyardView.tsx';
import { FleetView } from '@galaxy/client/components/FleetView.tsx';
import { CombatView } from '@galaxy/client/components/CombatView.tsx';
import { GalaxyMapView } from '@galaxy/client/components/GalaxyMapView.tsx';
import { FleetMovementBar } from '@galaxy/client/components/FleetMovementBar.tsx';
import { AllianceView } from '@galaxy/client/components/AllianceView.tsx';
import { StarsShopView } from '@galaxy/client/components/StarsShopView.tsx';
import { DailyRewardsAndBoostsView } from '@galaxy/client/components/DailyRewardsAndBoostsView.tsx';
import { getTelegramWebApp } from '@galaxy/client/telegram/sdk.ts';

interface GalaxyGameProps {
  onBackToHub: () => void;
}

const GalaxyContent: React.FC<{ onBackToHub: () => void }> = ({ onBackToHub }) => {
  const { state, loading, error, notification, authStatus, authError, retryAuth } = useGame();
  const [activeTab, setActiveTab] = useState<TabType>('base');

  if (loading) {
    return (
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        height: '100vh',
        gap: 16,
        background: 'var(--bg-space, #050811)',
      }}>
        <div style={{
          width: 48,
          height: 48,
          border: '3px solid var(--border-panel, #172a54)',
          borderTop: '3px solid var(--accent-cyan, #00e5ff)',
          borderRadius: '50%',
          animation: 'spin 1s linear infinite',
        }} />
        <div className="mono" style={{ color: 'var(--accent-cyan, #00e5ff)', fontSize: 13, letterSpacing: 1 }}>
          {authStatus === 'authenticating' ? 'ĐANG KẾT NỐI HỆ THỐNG ĐẾ CHẾ THIÊN HÀ...' : 'ĐANG TẢI DỮ LIỆU CĂN CỨ VŨ TRỤ...'}
        </div>
      </div>
    );
  }

  if (authStatus === 'expired') {
    return (
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        height: '100vh',
        padding: 24,
        textAlign: 'center',
        background: 'var(--bg-space, #050811)',
      }}>
        <div style={{ fontSize: 44, marginBottom: 12 }}>⚠️</div>
        <h2 style={{ color: 'var(--status-yellow, #eab308)', marginBottom: 8, fontSize: 16 }}>
          PHIÊN ĐĂNG NHẬP TELEGRAM CẦN LÀM MỚI
        </h2>
        <p style={{ color: 'var(--text-dim, #8ba2c4)', fontSize: 13, marginBottom: 20, maxWidth: 320, lineHeight: 1.5 }}>
          {authError || 'Chữ ký xác thực Telegram cần được làm mới. Vui lòng bấm thử lại hoặc mở lại ứng dụng từ @Gamengayxua_bot.'}
        </p>
        <div style={{ display: 'flex', gap: 12 }}>
          <button className="sci-btn" onClick={() => retryAuth()}>
            THỬ LẠI
          </button>
          <button
            className="sci-btn"
            style={{ background: 'transparent', borderColor: 'var(--border-panel, #172a54)' }}
            onClick={onBackToHub}
          >
            VỀ GAMEHUB
          </button>
        </div>
      </div>
    );
  }

  if (error && !state) {
    return (
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        height: '100vh',
        padding: 24,
        textAlign: 'center',
        background: 'var(--bg-space, #050811)',
      }}>
        <h2 style={{ color: 'var(--status-red, #ff1744)', marginBottom: 8 }}>MẤT KẾT NỐI MÁY CHỦ</h2>
        <p style={{ color: 'var(--text-dim, #8ba2c4)', fontSize: 13, marginBottom: 16 }}>{error}</p>
        <div style={{ display: 'flex', gap: 12 }}>
          <button className="sci-btn" onClick={() => retryAuth()}>
            TẢI LẠI
          </button>
          <button
            className="sci-btn"
            style={{ background: 'transparent', borderColor: 'var(--border-panel, #172a54)' }}
            onClick={onBackToHub}
          >
            VỀ GAMEHUB
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', minHeight: '100vh', position: 'relative', background: 'var(--bg-space, #050811)' }}>
      {/* Top Bar for GameHub Integration */}
      <div style={{
        position: 'sticky',
        top: 0,
        zIndex: 1100,
        background: 'rgba(5, 8, 17, 0.95)',
        borderBottom: '1px solid var(--border-panel, #172a54)',
        paddingTop: 'calc(10px + env(safe-area-inset-top, 0px))',
        paddingBottom: '8px',
        paddingLeft: '14px',
        paddingRight: '14px',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        backdropFilter: 'blur(12px)',
        WebkitBackdropFilter: 'blur(12px)',
      }}>
        <button
          onClick={onBackToHub}
          style={{
            background: 'rgba(14, 24, 52, 0.9)',
            border: '1px solid var(--accent-cyan, #00e5ff)',
            color: 'var(--text-bright, #e6f2ff)',
            padding: '4px 10px',
            borderRadius: 4,
            fontSize: 11,
            fontWeight: 700,
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: 4,
          }}
        >
          <span>←</span>
          <span>VỀ GAMEHUB</span>
        </button>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <span style={{ color: 'var(--accent-cyan, #00e5ff)', fontSize: 11, fontWeight: 800, letterSpacing: 0.5 }}>
            🚀 ĐẾ CHẾ THIÊN HÀ
          </span>
          <span style={{
            fontSize: 9,
            padding: '2px 6px',
            background: 'rgba(0, 229, 255, 0.15)',
            border: '1px solid var(--accent-cyan, #00e5ff)',
            borderRadius: 3,
            color: 'var(--accent-cyan, #00e5ff)',
            fontWeight: 700,
          }}>
            FULL ENGINE
          </span>
        </div>
      </div>

      {/* Floating Notification */}
      {notification && (
        <div style={{
          position: 'fixed',
          bottom: 70,
          left: '50%',
          transform: 'translateX(-50%)',
          background: 'rgba(10, 17, 36, 0.95)',
          border: '1px solid var(--accent-cyan, #00e5ff)',
          boxShadow: '0 0 16px rgba(0, 229, 255, 0.4)',
          color: 'var(--text-bright, #e6f2ff)',
          padding: '8px 16px',
          borderRadius: 4,
          fontSize: 12,
          fontWeight: 700,
          zIndex: 1500,
          maxWidth: '92%',
          textAlign: 'center',
          backdropFilter: 'blur(8px)',
        }}>
          🔔 {notification}
        </div>
      )}

      {/* Galaxy Header (Metal, He3, Credits) */}
      <Header />

      {/* Active Fleet Movements Bar */}
      <FleetMovementBar />

      {/* Main View Area */}
      <main style={{ flex: 1, paddingBottom: 85 }}>
        {activeTab === 'base' && <BaseView />}
        {activeTab === 'galaxy' && <GalaxyMapView />}
        {activeTab === 'fleet' && <FleetView />}
        {activeTab === 'combat' && <CombatView />}
        {activeTab === 'shipyard' && <ShipyardView />}
        {activeTab === 'research' && <ResearchView />}
        {activeTab === 'alliance' && <AllianceView />}
        {activeTab === 'daily' && <DailyRewardsAndBoostsView onOpenShop={() => setActiveTab('shop')} />}
        {activeTab === 'shop' && <StarsShopView />}
      </main>

      {/* Bottom Navigation */}
      <Navigation activeTab={activeTab} onSelectTab={setActiveTab} />
    </div>
  );
};

export const GalaxyGame: React.FC<GalaxyGameProps> = ({ onBackToHub }) => {
  return (
    <GameProvider>
      <GalaxyContent onBackToHub={onBackToHub} />
    </GameProvider>
  );
};

export default GalaxyGame;
